import { forwardRef, type JSX, type PropsWithChildren, useCallback } from 'react'
import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip'

import resolveTailwindClasses from '../utils/resolveTailwindClasses'
import { TooltipColor, type TooltipProps } from './types'

export const TooltipProvider = Provider

type TooltipRootProps = Omit<TooltipProps, 'hasProvider' | 'disabled' | 'providerProps'>

const TooltipRoot = forwardRef<HTMLButtonElement, PropsWithChildren<TooltipRootProps>>(
  (
    {
      children,
      content,
      rootProps,
      color = 'dark',
      isError = false,
      showArrow = false,
      ...props
    }: PropsWithChildren<TooltipRootProps>,
    ref
  ): JSX.Element => {
    const contentColorClasses = useCallback(() => {
      switch (color) {
        case TooltipColor.light:
          return 'bg-primary-200 text-primary-800'
        case TooltipColor.dark:
          return 'bg-primary-600 text-primary-100'
        default:
          console.warn(`Unknown button color: ${color}`)

          return ''
      }
    }, [color, isError])

    const arrowColorClasses = useCallback(() => {
      switch (color) {
        case TooltipColor.light:
          return 'fill-primary-200'
        case TooltipColor.dark:
          return 'fill-primary-600'
        default:
          return ''
      }
    }, [color])

    return (
      <Root {...(rootProps || {})}>
        <Trigger ref={ref} asChild>
          {children}
        </Trigger>

        <Portal>
          <Content
            side="top"
            sideOffset={8}
            {...props}
            className={resolveTailwindClasses(
              'z-50 whitespace-pre-wrap rounded p-px px-1 text-xs',
              contentColorClasses(),
              props.className
            )}
          >
            {content}
            {showArrow && <Arrow className={arrowColorClasses()} />}
          </Content>
        </Portal>
      </Root>
    )
  }
)

TooltipRoot.displayName = 'TooltipRoot'

const Tooltip = forwardRef<HTMLButtonElement, PropsWithChildren<TooltipProps>>(
  ({ content, children, providerProps, disabled = false, hasProvider = false, ...props }, ref): JSX.Element => {
    if (disabled || !content) return <>{children}</>

    if (hasProvider)
      return (
        <TooltipRoot ref={ref} content={content} {...props}>
          {children}
        </TooltipRoot>
      )

    return (
      <TooltipProvider {...(providerProps || {})}>
        <TooltipRoot ref={ref} content={content} {...props}>
          {children}
        </TooltipRoot>
      </TooltipProvider>
    )
  }
)

Tooltip.displayName = 'Tooltip'

export { Tooltip }
